import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { useYardContext } from "@/providers/yard-context";
import {
  multipartSerializer,
  pickFile,
} from "../dossier-marche/dossier-marche-panels";

type Ftm =
  paths["/api/yards/{yardId}/ftms"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

type FtmsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; items: Ftm[] };

type OsFormData = {
  number: string;
  dateOs: string;
  // Checkbox values: ids of the FTMs the OS confirms.
  ftmIds: string[];
  // Keyed `doc-<id>`: a bare numeric key would make react-hook-form build an
  // array.
  plans: Record<string, "confirme" | "infirme">;
};

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";
const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";
const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";

export type RegisterOsPanelProps = {
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Consigner la réception d'un OS (écran 003) : n°, date, fichier, puis les FTM
 * dont l'OS confirme la commande et, pour chacune, les plans confirmés (ils
 * amendent le dossier marché) ou infirmés.
 */
export function RegisterOsPanel({ onClose, onSaved }: RegisterOsPanelProps) {
  const { yard } = useYardContext();
  const [ftms, setFtms] = useState<FtmsState>({ status: "loading" });
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<OsFormData>({
    defaultValues: { number: "", dateOs: "", ftmIds: [], plans: {} },
  });
  const selectedFtmIds = form.watch("ftmIds");

  useEffect(() => {
    async function fetchFtms() {
      try {
        const { data } = await apiClient.GET("/api/yards/{yardId}/ftms", {
          params: { path: { yardId: String(yard.id) } },
        });
        setFtms(
          isNil(data)
            ? { status: "error" }
            : { status: "success", items: data.data.items },
        );
      } catch (error) {
        console.error("Failed to load FTMs", error);
        setFtms({ status: "error" });
      }
    }
    fetchFtms();
  }, [yard.id]);

  const onSubmit = form.handleSubmit(async (data) => {
    if (ftms.status !== "success") return;
    setSubmitting(true);
    try {
      const metadata = {
        number: data.number,
        dateOs: new Date(data.dateOs).toISOString(),
        ftms: ftms.items
          .filter((ftm) => data.ftmIds.includes(String(ftm.id)))
          .map((ftm) => ({
            ftmId: ftm.id,
            confirmedDocumentIds: ftm.documents
              .filter((doc) => data.plans[`doc-${doc.id}`] === "confirme")
              .map((doc) => doc.id),
          })),
      };
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/ordres-service",
        {
          params: { path: { yardId: String(yard.id) } },
          body: { metadata: JSON.stringify(metadata), file: file ?? undefined },
          bodySerializer: multipartSerializer,
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error ?? "Échec de l'enregistrement de l'OS",
        });
        return;
      }
      toaster.success({ title: "Succès", description: "OS enregistré" });
      onSaved();
    } catch (err) {
      console.error("OS registration failed", err);
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <form onSubmit={onSubmit} className="space-y-4">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Enregistrer la réception d'un OS
        </h3>

        <div>
          <label htmlFor="osNumber" className={labelClass}>
            N° de l'OS <span className="text-red-500">*</span>
          </label>
          <input
            id="osNumber"
            type="text"
            {...form.register("number")}
            required
            maxLength={255}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="osDate" className={labelClass}>
            Date de l'OS <span className="text-red-500">*</span>
          </label>
          <input
            id="osDate"
            type="date"
            {...form.register("dateOs")}
            required
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="osFile" className={labelClass}>
            Fichier de l'OS
          </label>
          <input
            id="osFile"
            type="file"
            accept={ALLOWED_DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(",")}
            onChange={pickFile(
              setFile,
              ALLOWED_DOCUMENT_EXTENSIONS,
              MAX_PDF_SIZE_BYTES,
              "50 Mo",
            )}
            className={fileInputClass}
          />
        </div>

        <fieldset className="space-y-2">
          <legend className={labelClass}>
            FTM confirmées par l'OS <span className="text-red-500">*</span>
          </legend>
          {ftms.status === "loading" ? (
            <p className="text-sm text-zinc-500">Chargement des FTM…</p>
          ) : ftms.status === "error" ? (
            <p className="text-sm text-red-600">
              Impossible de charger les FTM du chantier.
            </p>
          ) : ftms.items.length === 0 ? (
            <p className="text-sm text-zinc-500">
              Aucune FTM enregistrée sur ce chantier.
            </p>
          ) : (
            <ul className="space-y-2">
              {ftms.items.map((ftm) => (
                <li key={ftm.id}>
                  <label className="flex items-center gap-2 text-sm text-zinc-900 dark:text-zinc-100">
                    <input
                      type="checkbox"
                      value={String(ftm.id)}
                      {...form.register("ftmIds")}
                    />
                    {`FTM n°${ftm.number} - ${ftm.object}`}
                  </label>
                  {selectedFtmIds.includes(String(ftm.id)) && (
                    <FtmPlans ftm={ftm} form={form} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" outline onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={submitting || selectedFtmIds.length === 0}
          >
            {submitting ? "En cours..." : "Enregistrer l'OS"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FtmPlans({
  ftm,
  form,
}: {
  ftm: Ftm;
  form: UseFormReturn<OsFormData>;
}) {
  if (ftm.documents.length === 0) {
    return (
      <p className="mt-1 pl-6 text-sm text-zinc-500">
        Aucun plan associé à cette FTM.
      </p>
    );
  }
  return (
    <ul className="mt-1 space-y-1 pl-6">
      {ftm.documents.map((doc) => {
        const label = isNil(doc.indice)
          ? `${doc.number} - ${doc.title}`
          : `${doc.number} - ${doc.title} (indice ${doc.indice})`;
        return (
          <li key={doc.id}>
            {/* A fieldset defaults to min-width: min-content, which lets a
                long plan title push the radios out of the drawer. */}
            <fieldset className="min-w-0 text-sm">
              <legend className="sr-only">{label}</legend>
              <span className="block truncate" title={label}>
                {label}
              </span>
              <div className="flex gap-4">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="confirme"
                    {...form.register(`plans.doc-${doc.id}`)}
                    required
                  />
                  Confirmé
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="infirme"
                    {...form.register(`plans.doc-${doc.id}`)}
                    required
                  />
                  Infirmé
                </label>
              </div>
            </fieldset>
          </li>
        );
      })}
    </ul>
  );
}
